<script setup lang="ts">
import { reactive, computed } from 'vue';
import { useAreaStore } from '@/stores/areas';
import { useKioskConfigStore } from '@/stores/kioskConfig';
import { useTimesheetStore } from '@/stores/timesheets';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import { getTranslations } from '@/lib/util/i18n';
import { CoIcon } from '@deputy/copilot2';
import LoadingSpinner from './LoadingSpinner.vue';
import SelectListSearch from '@/components/SelectListSearch.vue';
import BreaksList from './BreaksList.vue';

const areas = useAreaStore();
const kioskConfig = useKioskConfigStore();
const timesheets = useTimesheetStore();
const timesheetSetup = useTimesheetSetupStore();

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'select-list-modal-content',
});

const state = reactive({
  searchTerm: '',
});

function startBreak(breakType: string, breakId?: number, source?: string) {
  emit('startBreak', breakType, breakId, source);
}

defineProps<{
  showBreakOptionsModal: boolean;
  showAreasModal: boolean;
  showEditAreasModal: boolean;
}>();

const emit = defineEmits<{
  (
    e: 'startShift',
    areaId: number,
    shiftQuestionsResponses?: Record<string, string>
  ): void;
  (e: 'startBreak', breakType: string, breakId?: number, source?: string): void;
  (e: 'areaSelected', areaId: number): void;
}>();

function search(searchTerm: string) {
  state.searchTerm = searchTerm;
}

const areasList = computed(() => areas.searchAreas(state.searchTerm));
</script>

<template>
  <LoadingSpinner
    v-if="areas.isLoading || timesheets.isLoading || timesheetSetup.isLoading"
    class="__spinner"
    data-test-id="loading-areas"
  ></LoadingSpinner>
  <div v-else-if="!areas.errors.loadingError">
    <SelectListSearch
      v-if="showAreasModal || showEditAreasModal"
      class="__search"
      :placeholder="t('areas-list:search-placeholder')"
      @search="search"
    ></SelectListSearch>
    <ul>
      <div v-if="showAreasModal || showEditAreasModal" class="__areas-list">
        <div
          data-test-id="area"
          v-for="(area, index) in areasList"
          :key="area.Id"
        >
          <li @click="emit('areaSelected', area.Id)">
            <span class="__icon-background"
              ><CoIcon :style="{ color: area.Colour }" name="briefcase"></CoIcon
            ></span>
            <a v-if="kioskConfig.data.KioskAppConfigs.ENABLE_MULTI_LOCATIONS">
              {{ area.OperationalUnitName }} ({{ area.CompanyName }})
            </a>
            <a v-else>{{ area.OperationalUnitName }}</a>
          </li>
          <hr v-if="index != areasList.length - 1 && areasList.length > 1" />
        </div>
        <div class="__empty-list" v-if="!areasList.length">
          {{ t('areas-list:empty') }}
        </div>
      </div>

      <div
        v-else-if="showBreakOptionsModal && timesheets.activeTimesheet"
        class="__break-options"
      >
        <BreaksList
          @startBreak="startBreak"
          :selectBreaksModal="true"
          :scheduledBreaks="true"
          :breaks="timesheets.getAllScheduledBreaks()"
        ></BreaksList>
        <li
          v-if="!timesheets.getUnfinishedUnpaidScheduledBreaks().length"
          data-test-id="start-unpaid-break"
          @click="
            emit(
              'startBreak',
              kioskConfig.getUnpaidBreakInformation!.SlotValue,
              timesheets.getScheduledBreakId(
                kioskConfig.getUnpaidBreakInformation!.SlotValue
              ),
              'selectBreakType'
            )
          "
        >
          <span class="__icon-background __pink">
            <img class="__icon-image" src="../assets/images/cafe-pink.png" />
          </span>
          <a>
            {{ kioskConfig.getUnpaidBreakInformation?.SlotName
            }}{{ t('break-options:unpaid-break') }}
          </a>
        </li>
        <hr
          v-if="
            !timesheets.getUnfinishedPaidScheduledBreaks().length &&
            !timesheets.getUnfinishedUnpaidScheduledBreaks().length
          "
        />
        <li
          v-if="!timesheets.getUnfinishedPaidScheduledBreaks().length"
          data-test-id="start-paid-break"
          @click="
            emit(
              'startBreak',
              kioskConfig.getPaidBreakInformation!.SlotValue,
              timesheets.getScheduledBreakId(
                kioskConfig.getPaidBreakInformation!.SlotValue
              ),
              'selectBreakType'
            )
          "
        >
          <span class="__icon-background __green">
            <img class="__icon-image" src="../assets/images/cafe-green.png" />
          </span>
          <a>
            {{ kioskConfig.getPaidBreakInformation?.SlotName
            }}{{ t('break-options:paid-break') }}
          </a>
        </li>
      </div>
    </ul>
  </div>

  <div data-test-id="error" v-else>{{ t('areas-list:loading-failed') }}</div>
</template>

<style scoped>
.__spinner {
  display: flex;
  margin: 100px;
}
.__search {
  margin-bottom: 20px;
}
ul {
  list-style-type: none;
  padding-left: 0;
}
li {
  display: flex;
  align-items: center;
}
li:hover {
  cursor: pointer;
}
hr {
  margin: 10px 0 10px 60px;
  border: none;
  height: 1px;
  background-color: var(--co-lines-border-solid);
}
.__icon-background {
  display: flex;
  padding: 10px;
  border-radius: 100px;
  margin-right: 18px;
  justify-content: center;
  align-items: center;
  float: left;
}

.__areas-list .__icon-background {
  background: #f3f3f7;
}
.__areas-list .co-icon {
  color: var(--co-accent-tertiary);
  width: 1.5rem;
  height: 1.5rem;
}
.__empty-list {
  color: var(--co-text-primary);
  text-align: center;
  margin: 10px auto;
}

.__break-options li {
  font-size: 16px;
}
.__break-options .__icon-background {
  width: 45px;
}

.__break-options .__pink {
  background: var(--co-spot-icon-pink-background);
}
.__break-options .__green {
  background: var(--co-spot-icon-teal-background);
}
.__break-options .__icon-image {
  width: 1.3rem;
  height: 1.4rem;
}
</style>
